import React from 'react';
import { motion } from 'framer-motion';
import './Quality.css';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

const Quality = () => {
  const certs = [
    {
      icon: '🏅', name: 'FSSAI', status: 'Active', statusClass: 'active',
      desc: 'Food Safety & Standards Authority of India. Ensuring our products meet India\'s highest food safety and hygiene regulations before export.',
      points: ['Food safety compliance', 'Hygiene standards', 'Regular inspections', 'Consumer protection'],
    },
    {
      icon: '🌾', name: 'APEDA', status: 'Registered', statusClass: 'active',
      desc: 'Agricultural & Processed Food Products Export Development Authority. Registered for full agro-export compliance and government-recognized trade.',
      points: ['Export authorization', 'Quality monitoring', 'Market development', 'Trade facilitation'],
    },
    {
      icon: '📋', name: 'IEC', status: 'Certified 2026', statusClass: 'active',
      desc: 'Import Export Code granted by DGFT (Directorate General of Foreign Trade). Our official government authorization for international trade operations.',
      points: ['DGFT registered', 'International trade auth', 'Customs clearance', 'Bank forex support'],
    },
    {
      icon: '🔬', name: 'ISO', status: 'In Progress', statusClass: 'planned',
      desc: 'ISO Quality Management System (ISO 9001) certification actively being pursued to further strengthen our international credibility.',
      points: ['Process standardization', 'Quality management', 'Continuous improvement', 'Global recognition'],
    },
  ];

  const pkgStandards = [
    {
      cat: 'Frozen Foods', icon: '🥶',
      options: ['PET/PE Pouches', 'BOPP Pouches', 'Corrugated Fiberboard Boxes', 'Vacuum Packing'],
      note: 'Temperature-controlled, moisture-proof packaging maintaining -18°C cold chain integrity.',
    },
    {
      cat: 'Organic Spices', icon: '🌶️',
      options: ['Double PP Bags', 'Corrugated Boxes', 'PP Bags'],
      note: 'Airtight double-layer packaging preserving aroma, volatile oils, and preventing contamination.',
    },
    {
      cat: 'Foxnuts (Makhana)', icon: '🍿',
      options: ['Matte Zipper 100g Retail Pouches', 'Corrugated Boxes (Bulk)'],
      note: 'Premium matte zipper retail-ready pouches for shelf appeal; bulk corrugated for wholesale.',
    },
    {
      cat: 'Dehydrated Powders', icon: '🧅',
      options: ['METPET Pouches', 'HDPE Bags (Bulk)', 'Vacuum Packing'],
      note: 'Multi-layer METPET ensures moisture barrier; HDPE for large bulk orders with UV protection.',
    },
  ];

  const qcSteps = [
    { step: '01', title: 'Raw Material Inspection', desc: 'Every incoming batch is inspected for freshness, grade, and contamination before processing.' },
    { step: '02', title: 'Processing Standards', desc: 'Products processed under hygienic conditions with temperature and humidity control throughout.' },
    { step: '03', title: 'Pre-Shipment Testing', desc: 'Lab testing for moisture, foreign matter, microbiological compliance, and ASTA standards.' },
    { step: '04', title: 'Packaging Integrity', desc: 'Each packaging unit checked for seal quality, labeling accuracy, and export compliance.' },
    { step: '05', title: 'Documentation Review', desc: 'All export documents — COA, FSSAI, Phytosanitary, APEDA — verified before shipment.' },
    { step: '06', title: 'Shipment Clearance', desc: 'Final quality sign-off before customs clearance and dispatch to ensure buyer satisfaction.' },
  ];

  return (
    <main className="quality-page">
      <section className="quality-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
              Quality Assurance
            </div>
            <h1 className="section-title" style={{ color: 'var(--white)', fontSize: 'clamp(30px,5vw,52px)' }}>
              Certified. Compliant. Trusted.
            </h1>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Every product that leaves our facility meets strict food safety and international export compliance standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CERTS */}
      <section style={{ padding: '80px 0', background: 'var(--white)' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ marginBottom: '40px' }}>
            <div className="section-tag">Certifications</div>
            <h2 className="section-title">Our Certifications & Registrations</h2>
          </motion.div>
          <div className="certs-grid">
            {certs.map((c, i) => (
              <motion.div
                key={c.name}
                className="cert-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                variants={fadeUp}
              >
                <div className="cert-icon">{c.icon}</div>
                <div className="cert-name">{c.name}</div>
                <span className={`cert-badge ${c.statusClass}`}>{c.status}</span>
                <p className="cert-desc">{c.desc}</p>
                <ul className="cert-points">
                  {c.points.map(pt => <li key={pt}>✓ {pt}</li>)}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QC PROCESS */}
      <section style={{ padding: '80px 0', background: 'var(--off-white)' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ marginBottom: '48px' }}>
            <div className="section-tag">Quality Control</div>
            <h2 className="section-title">Our 6-Step Quality Process</h2>
            <p className="section-sub">Every order goes through a rigorous quality control journey before reaching your port.</p>
          </motion.div>
          <div className="qc-grid">
            {qcSteps.map((s, i) => (
              <motion.div
                key={s.step}
                className="qc-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.08}
                variants={fadeUp}
              >
                <div className="qc-step">{s.step}</div>
                <div className="qc-title">{s.title}</div>
                <div className="qc-desc">{s.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGING */}
      <section style={{ padding: '80px 0', background: 'var(--white)' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ marginBottom: '40px' }}>
            <div className="section-tag">Packaging Standards</div>
            <h2 className="section-title">Export-Grade Packaging by Category</h2>
          </motion.div>
          <div className="pkg-grid">
            {pkgStandards.map((p, i) => (
              <motion.div
                key={p.cat}
                className="pkg-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                variants={fadeUp}
              >
                <div className="pkg-cat-icon">{p.icon}</div>
                <div className="pkg-cat-name">{p.cat}</div>
                <div className="pkg-options">
                  {p.options.map(o => <span key={o} className="pkg-badge">{o}</span>)}
                </div>
                <p className="pkg-note">{p.note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCUMENTS */}
      <section style={{ padding: '80px 0', background: 'var(--green)' }}>
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div className="section-tag" style={{ background: 'rgba(255,255,255,0.12)', color: 'var(--gold-light)', margin: '0 auto 16px' }}>
              Documentation
            </div>
            <h2 className="section-title" style={{ color: 'var(--white)' }}>Export Documents We Provide</h2>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.7)', margin: '0 auto' }}>
              Complete documentation support for seamless customs clearance at your port of destination.
            </p>
          </motion.div>
          <div className="docs-grid">
            {[
              '📄 Certificate of Origin', '🌱 Phytosanitary Certificate', '🏅 FSSAI Certificate',
              '🌾 APEDA Certificate', '🔬 Certificate of Analysis (COA)', '📦 Packing List',
              '🚢 Bill of Lading', '💰 Commercial Invoice', '📋 IEC Certificate Copy',
            ].map((doc, i) => (
              <motion.div
                key={doc}
                className="doc-badge"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.06}
                variants={fadeUp}
              >
                {doc}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Quality;
